import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, Loader2, Receipt } from 'lucide-react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import api from '../../services/api';
import { cs } from '../../lib/currency';
import { InvoiceStatement } from '../admin/invoice/InvoiceStatement';
import PayWithCreditButton from './PayWithCreditButton';

const STATUS_STYLE = {
  paid: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  overdue: 'bg-red-50 text-red-700 border-red-200',
  failed: 'bg-red-50 text-red-700 border-red-200',
  partial: 'bg-amber-50 text-amber-700 border-amber-200',
  pending: 'bg-slate-50 text-slate-700 border-slate-200',
  processing: 'bg-blue-50 text-blue-700 border-blue-200',
  void: 'bg-slate-100 text-slate-500 border-slate-200',
  refunded: 'bg-purple-50 text-purple-700 border-purple-200',
  partially_refunded: 'bg-purple-50 text-purple-700 border-purple-200',
};
const STATUS_FILTERS = [
  ['all', 'All statuses'], ['open', 'Needs payment'], ['processing', 'Processing'], ['paid', 'Paid'],
  ['refunded', 'Refunded'], ['void', 'Voided'],
];
const OPEN = new Set(['overdue', 'pending', 'partial', 'failed']);
const money = (n) => `${cs()}${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const typeLabel = (inv) => inv.facts?.find((f) => f.label === 'Invoice type')?.value || inv.type_label || 'Invoice';
const isOther = (inv) => inv.type === 'manual' && inv.invoice_type !== 'uniform' && typeLabel(inv).startsWith('Invoice');
const category = (inv) => (isOther(inv) ? 'Other invoices' : typeLabel(inv));
const ZERO = new Set(['void', 'cancelled', 'superseded']);

const AmountCell = ({ inv, acc }) => {
  if (ZERO.has(inv.status)) {
    return (
      <div data-testid={`parent-invoice-balance-${inv.id}`}>
        <p className="text-xs text-slate-400 line-through tabular-nums">{money(acc.fee_total)}</p>
        <p className="text-sm font-medium text-slate-600">Nothing owed</p>
      </div>
    );
  }
  return (
    <p className="font-semibold tabular-nums" data-testid={`parent-invoice-balance-${inv.id}`}>
      {acc.balance_remaining > 0 ? `${money(acc.balance_remaining)} due` : money(acc.fee_total)}
    </p>
  );
};

const matchStatus = (inv, f) => {
  const s = inv.status;
  if (f === 'all') return true;
  if (f === 'open') return OPEN.has(s);
  if (f === 'refunded') return s === 'refunded' || s === 'partially_refunded';
  return s === f;
};

const InvoiceCard = ({ inv, onPay, onPaid }) => {
  const [open, setOpen] = useState(OPEN.has(inv.status));
  const acc = inv.accounting || {};
  const title = isOther(inv) ? typeLabel(inv) : (inv.tournament_name || inv.description || typeLabel(inv));
  const canPay = Boolean(inv.pay_id);
  return (
    <div className="border border-slate-200 rounded-lg bg-white" data-testid={`parent-invoice-${inv.id}`}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-start justify-between gap-3 p-4 text-left"
        data-testid={`parent-invoice-toggle-${inv.id}`}>
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wide text-slate-500">{isOther(inv) ? 'Invoice' : typeLabel(inv)}</p>
          <p className="font-medium text-slate-900 truncate">{title}</p>
          <p className="text-xs text-slate-500 mt-0.5">
            {[inv.player_names, String(inv.invoice_date || inv.created_at || '').slice(0, 10), inv.invoice_number].filter(Boolean).join(' · ')}
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="text-right">
            <AmountCell inv={inv} acc={acc} />
            <span className={`inline-block mt-1 text-[11px] px-2 py-0.5 rounded-full border ${STATUS_STYLE[inv.status] || STATUS_STYLE.pending}`}
              data-testid={`parent-invoice-status-${inv.id}`}>
              {String(inv.status || '').replace(/_/g, ' ').toUpperCase()}
            </span>
          </div>
          {open ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4 grid gap-4 md:grid-cols-2">
          <dl className="text-sm space-y-1.5" data-testid={`parent-invoice-facts-${inv.id}`}>
            {(inv.facts || []).map((f) => (
              <div key={f.label} className="flex justify-between gap-4">
                <dt className="text-slate-500">{f.label}</dt><dd className="text-slate-900 text-right">{f.value}</dd>
              </div>
            ))}
            {(acc.items || []).length > 0 && (
              <div className="pt-2 border-t" data-testid={`parent-invoice-items-${inv.id}`}>
                {acc.items.map((it, i) => (
                  <div key={i} className="flex justify-between gap-4 text-xs text-slate-700">
                    <span>{it.description} × {it.quantity}</span><span className="tabular-nums">{money(it.unit_price)} each · {money(it.amount)}</span>
                  </div>
                ))}
              </div>
            )}
          </dl>
          <div className="space-y-3">
            <InvoiceStatement accounting={acc} title="" testId={`parent-invoice-statement-${inv.id}`} />
            {canPay && (
              <div className="flex flex-wrap justify-end gap-2">
                {inv.credit_entity && (
                  <PayWithCreditButton entityType={inv.credit_entity[0]} entityId={inv.credit_entity[1]}
                    amount={acc.balance_remaining} description={title} buttonText="Use Credit"
                    testId={`parent-invoice-credit-${inv.id}`} onSuccess={onPaid} />
                )}
                <Button size="sm" className={inv.status === 'overdue' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}
                  onClick={() => onPay(inv)} data-testid={`parent-invoice-pay-${inv.id}`}>
                  Pay {money(acc.balance_remaining)}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// iter396 — every invoice the family has (all types, all statuses) with the same statement admins see.
export const ParentInvoiceList = ({ onPay, refreshKey, startDate, endDate }) => {
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState('all');
  const [type, setType] = useState('all');
  const [tick, setTick] = useState(0);
  useEffect(() => {
    api.get('/parent/invoices/all').then((r) => setRows(r.data?.invoices || [])).catch(() => setRows([]));
  }, [refreshKey, tick]);
  const types = useMemo(() => [...new Set((rows || []).map(category))], [rows]);
  const inRange = (r) => {
    const d = String(r.invoice_date || r.created_at || '').slice(0, 10);
    return (!startDate || d >= startDate) && (!endDate || d <= endDate);
  };
  const shown = (rows || []).filter((r) => matchStatus(r, status) && (type === 'all' || category(r) === type) && inRange(r));
  const today = new Date().toLocaleDateString('en-CA');
  const isDueNow = (r) => String(r.invoice_date || r.created_at || '').slice(0, 10) <= today;
  const openShown = shown.filter((r) => OPEN.has(r.status));
  const bal = (r) => Number(r.accounting?.balance_remaining || 0);
  const due = openShown.filter(isDueNow).reduce((s, r) => s + bal(r), 0);
  const later = openShown.filter((r) => !isDueNow(r)).reduce((s, r) => s + bal(r), 0);
  if (rows === null) return <div className="p-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;
  return (
    <div className="p-4 space-y-4" data-testid="parent-invoice-list">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-8 w-44 text-sm" data-testid="parent-invoice-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{STATUS_FILTERS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="h-8 w-52 text-sm" data-testid="parent-invoice-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All invoice types</SelectItem>
            {types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <p className="text-sm text-slate-600 ml-auto" data-testid="parent-invoice-total-due">
          {shown.length} invoice{shown.length === 1 ? '' : 's'} · Due now {money(due)}{later > 0 ? ` · ${money(later)} not yet due` : ''}
        </p>
      </div>
      {shown.length === 0 ? (
        <div className="text-center py-10 text-slate-500"><Receipt className="h-8 w-8 mx-auto mb-2 text-slate-300" />No invoices match these filters</div>
      ) : shown.map((inv) => (
        <InvoiceCard key={`${inv.type}-${inv.id}`} inv={inv} onPay={onPay} onPaid={() => setTick((t) => t + 1)} />
      ))}
    </div>
  );
};

export default ParentInvoiceList;
